import React, { useState, useEffect } from 'react';
import { Bell, Save, Plus, X, AlertCircle, CheckCircle, Clock, Trash2, Play, Calendar, Activity, Ticket } from 'lucide-react';
import { format } from 'date-fns';
import clsx from 'clsx';
import { apiService } from '../services/api';

const FREQUENCY_OPTIONS = [
  { value: 1, label: 'Every 1 hour' },
  { value: 2, label: 'Every 2 hours' },
  { value: 4, label: 'Every 4 hours' },
  { value: 6, label: 'Every 6 hours' },
  { value: 12, label: 'Every 12 hours' },
  { value: 24, label: 'Daily (2 AM)' },
];

const SUITE_OPTIONS = [
  { value: 'all', label: 'All Tests' },
  { value: 'homepage', label: 'Homepage' },
  { value: 'collections', label: 'Collections' },
  { value: 'product', label: 'Product' },
  { value: 'top-navigation', label: 'Top Navigation' },
  { value: 'bottom-navigation', label: 'Bottom Navigation' },
  { value: 'used-gear-homepage', label: 'Used Gear - Homepage' },
  { value: 'used-gear-flows', label: 'Used Gear - Flows' },
  { value: 'used-gear-seo', label: 'Used Gear - SEO' },
];

const BROWSER_OPTIONS = [
  { value: 'chrome', label: 'Chrome' },
  { value: 'firefox', label: 'Firefox' },
  { value: 'safari', label: 'Safari' },
  { value: 'mobile-chrome', label: 'Mobile Chrome' },
  { value: 'mobile-safari', label: 'Mobile Safari' },
];

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Schedule state
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    test_suite: 'all',
    browser: 'chrome',
    frequency_hours: 1,
  });

  // Health check schedule state
  const [healthSchedule, setHealthSchedule] = useState(null);

  useEffect(() => {
    apiService.getNotificationSettings()
      .then((res) => setSettings(res.data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));

    fetchSchedules();

    apiService.getHealthCheckSchedule()
      .then((res) => setHealthSchedule(res.data))
      .catch(() => {});
  }, []);

  const updateHealthSchedule = async (changes) => {
    try {
      const res = await apiService.updateHealthCheckSchedule(changes);
      setHealthSchedule(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const fetchSchedules = () => {
    apiService.getSchedules()
      .then((res) => setSchedules(res.data))
      .catch(() => {})
      .finally(() => setSchedulesLoading(false));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await apiService.updateNotificationSettings(settings);
      setSettings(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Per-list "new email" input state, keyed by listKey
  const [newEmails, setNewEmails] = useState({});

  const addRecipient = (listKey, email) => {
    const e = (email || '').trim();
    if (!e || !e.includes('@')) return;
    const list = settings[listKey] || [];
    if (list.includes(e)) return;
    setSettings({ ...settings, [listKey]: [...list, e] });
    setNewEmails({ ...newEmails, [listKey]: '' });
  };

  const removeRecipient = (listKey, email) => {
    const list = settings[listKey] || [];
    setSettings({ ...settings, [listKey]: list.filter((r) => r !== email) });
  };

  const addHealthRecipient = (email) => {
    const e = (email || '').trim();
    if (!e || !e.includes('@')) return;
    const list = healthSchedule.recipients || [];
    if (list.includes(e)) return;
    updateHealthSchedule({ recipients: [...list, e] });
  };

  const removeHealthRecipient = (email) => {
    const list = healthSchedule.recipients || [];
    updateHealthSchedule({ recipients: list.filter((r) => r !== email) });
  };

  const handleAddSchedule = async () => {
    if (!newSchedule.name.trim()) return;
    try {
      await apiService.createSchedule(newSchedule);
      setShowAddSchedule(false);
      setNewSchedule({ name: '', test_suite: 'all', browser: 'chrome', frequency_hours: 1 });
      fetchSchedules();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const toggleSchedule = async (id, enabled) => {
    try {
      await apiService.updateSchedule(id, { enabled: !enabled });
      fetchSchedules();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const deleteSchedule = async (id) => {
    try {
      await apiService.deleteSchedule(id);
      fetchSchedules();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const runNow = async (id) => {
    try {
      await apiService.runScheduleNow(id);
      fetchSchedules();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configure schedules, notifications, and integrations
        </p>
      </div>

      {error && (
        <div className="bg-error-50 border border-error-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-error-500 shrink-0" />
          <p className="text-sm text-error-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-error-400 hover:text-error-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Health Check Schedule */}
      {healthSchedule && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <Activity className="h-5 w-5 text-gray-400" />
            <div>
              <h2 className="text-sm font-medium text-gray-800">Health Check Schedule</h2>
              <p className="text-xs text-gray-500">Automatically ping monitored URLs on a recurring schedule</p>
            </div>
          </div>
          <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <button
                onClick={() => updateHealthSchedule({ enabled: !healthSchedule.enabled })}
                className={clsx(
                  'relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0',
                  healthSchedule.enabled ? 'bg-primary-600' : 'bg-gray-300'
                )}
              >
                <span
                  className={clsx(
                    'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                    healthSchedule.enabled ? 'translate-x-4.5' : 'translate-x-0.5'
                  )}
                />
              </button>
              <div className="min-w-0">
                <p className={clsx('text-sm font-medium', healthSchedule.enabled ? 'text-gray-800' : 'text-gray-400')}>
                  {healthSchedule.enabled ? 'Enabled' : 'Paused'}
                </p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {healthSchedule.last_run_at && (
                    <span className="text-xs text-gray-400">
                      Last: {format(new Date(healthSchedule.last_run_at), 'MMM dd, HH:mm')}
                    </span>
                  )}
                  {healthSchedule.enabled && healthSchedule.next_run_at && (
                    <>
                      {healthSchedule.last_run_at && <span className="text-xs text-gray-300">|</span>}
                      <span className="text-xs text-gray-400">
                        Next: {format(new Date(healthSchedule.next_run_at), 'MMM dd, HH:mm')}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <select
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              value={healthSchedule.frequency_hours}
              onChange={(e) => updateHealthSchedule({ frequency_hours: parseInt(e.target.value) })}
            >
              {FREQUENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Notifications row */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4 bg-gray-50/50">
            <div className="flex items-center gap-4 min-w-0">
              <button
                onClick={() => updateHealthSchedule({ notify_enabled: !healthSchedule.notify_enabled })}
                className={clsx(
                  'relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0',
                  healthSchedule.notify_enabled ? 'bg-primary-600' : 'bg-gray-300'
                )}
              >
                <span
                  className={clsx(
                    'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                    healthSchedule.notify_enabled ? 'translate-x-4.5' : 'translate-x-0.5'
                  )}
                />
              </button>
              <div className="min-w-0">
                <p className={clsx('text-sm font-medium', healthSchedule.notify_enabled ? 'text-gray-800' : 'text-gray-400')}>
                  Email notifications {healthSchedule.notify_enabled ? 'on' : 'off'}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Recipients managed below — independent of test-run recipients
                </p>
              </div>
            </div>
            <select
              disabled={!healthSchedule.notify_enabled}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none disabled:bg-gray-100 disabled:text-gray-400"
              value={healthSchedule.notify_filter}
              onChange={(e) => updateHealthSchedule({ notify_filter: e.target.value })}
            >
              <option value="failures">Failures only</option>
              <option value="all">All checks</option>
            </select>
          </div>

          {/* Health-check recipients */}
          {healthSchedule.notify_enabled && (
            <div className="px-6 py-4 border-t border-gray-100 space-y-2 bg-gray-50/50">
              <p className="text-xs font-medium text-gray-700">Recipients for health-check emails</p>
              <div className="flex flex-wrap gap-2 min-h-[1.5rem]">
                {(healthSchedule.recipients || []).length === 0 && (
                  <span className="text-xs text-gray-400 italic">No recipients yet</span>
                )}
                {(healthSchedule.recipients || []).map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full text-xs"
                  >
                    {email}
                    <button
                      onClick={() => removeHealthRecipient(email)}
                      className="text-gray-400 hover:text-error-600"
                      title="Remove"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Add email address"
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  value={newEmails.health_recipients || ''}
                  onChange={(e) => setNewEmails({ ...newEmails, health_recipients: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addHealthRecipient(newEmails.health_recipients);
                      setNewEmails({ ...newEmails, health_recipients: '' });
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    addHealthRecipient(newEmails.health_recipients);
                    setNewEmails({ ...newEmails, health_recipients: '' });
                  }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Test Schedules */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-gray-400" />
            <div>
              <h2 className="text-sm font-medium text-gray-800">Test Schedules</h2>
              <p className="text-xs text-gray-500">Automatically run tests on a recurring schedule</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddSchedule(!showAddSchedule)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Schedule
          </button>
        </div>

        {/* Add schedule form */}
        {showAddSchedule && (
          <div className="p-6 border-b border-gray-100 bg-gray-50/50 space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Schedule Name</label>
              <input
                type="text"
                placeholder="e.g. New Gear - Hourly"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                value={newSchedule.name}
                onChange={(e) => setNewSchedule({ ...newSchedule, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Test Suite</label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  value={newSchedule.test_suite}
                  onChange={(e) => setNewSchedule({ ...newSchedule, test_suite: e.target.value })}
                >
                  {SUITE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Browser</label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  value={newSchedule.browser}
                  onChange={(e) => setNewSchedule({ ...newSchedule, browser: e.target.value })}
                >
                  {BROWSER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Frequency</label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  value={newSchedule.frequency_hours}
                  onChange={(e) => setNewSchedule({ ...newSchedule, frequency_hours: parseInt(e.target.value) })}
                >
                  {FREQUENCY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddSchedule(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSchedule}
                disabled={!newSchedule.name.trim()}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors',
                  newSchedule.name.trim() ? 'bg-primary-600 hover:bg-primary-700' : 'bg-gray-300 cursor-not-allowed'
                )}
              >
                Create Schedule
              </button>
            </div>
          </div>
        )}

        {/* Schedule list */}
        <div className="divide-y divide-gray-50">
          {schedulesLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent mx-auto" />
            </div>
          ) : schedules.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">
              No schedules configured. Add one to automatically run tests.
            </div>
          ) : (
            schedules.map((s) => (
              <div key={s.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50/50">
                <div className="flex items-center gap-4 min-w-0">
                  {/* Enable toggle */}
                  <button
                    onClick={() => toggleSchedule(s.id, s.enabled)}
                    className={clsx(
                      'relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0',
                      s.enabled ? 'bg-primary-600' : 'bg-gray-300'
                    )}
                  >
                    <span
                      className={clsx(
                        'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                        s.enabled ? 'translate-x-4.5' : 'translate-x-0.5'
                      )}
                    />
                  </button>
                  <div className="min-w-0">
                    <p className={clsx('text-sm font-medium truncate', s.enabled ? 'text-gray-800' : 'text-gray-400')}>
                      {s.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-gray-400">
                        {SUITE_OPTIONS.find((o) => o.value === s.test_suite)?.label || s.test_suite}
                      </span>
                      <span className="text-xs text-gray-300">|</span>
                      <span className="text-xs text-gray-400">
                        {BROWSER_OPTIONS.find((o) => o.value === s.browser)?.label || s.browser}
                      </span>
                      <span className="text-xs text-gray-300">|</span>
                      <span className="text-xs text-gray-400">
                        {FREQUENCY_OPTIONS.find((o) => o.value === s.frequency_hours)?.label || `${s.frequency_hours}h`}
                      </span>
                    </div>
                    {s.last_run_at && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Last run: {format(new Date(s.last_run_at), 'MMM dd, HH:mm')}
                        {s.next_run_at && s.enabled && (
                          <span> · Next: {format(new Date(s.next_run_at), 'MMM dd, HH:mm')}</span>
                        )}
                      </p>
                    )}
                    {!s.last_run_at && s.next_run_at && s.enabled && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Next run: {format(new Date(s.next_run_at), 'MMM dd, HH:mm')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-3">
                  <button
                    onClick={() => runNow(s.id)}
                    className="p-1.5 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                    title="Run now"
                  >
                    <Play className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => deleteSchedule(s.id)}
                    className="p-1.5 rounded-md text-gray-400 hover:text-error-600 hover:bg-error-50 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Email Notifications */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
          <Bell className="h-5 w-5 text-gray-400" />
          <div>
            <h2 className="text-sm font-medium text-gray-800">Email Notifications</h2>
            <p className="text-xs text-gray-500">Get notified when test runs complete</p>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-700">Enable email notifications</label>
            <button
              onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
              className={clsx(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                settings.enabled ? 'bg-primary-600' : 'bg-gray-300'
              )}
            >
              <span
                className={clsx(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  settings.enabled ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </div>

          {/* Test failures: toggle + dedicated recipients */}
          <RecipientGroup
            label="Test failures"
            description="Email when a scheduled or manual test run has any failed tests"
            toggleChecked={settings.on_failure}
            onToggle={(v) => setSettings({ ...settings, on_failure: v })}
            recipients={settings.failure_recipients || []}
            onAdd={(email) => addRecipient('failure_recipients', email)}
            onRemove={(email) => removeRecipient('failure_recipients', email)}
            inputValue={newEmails.failure_recipients || ''}
            onInputChange={(v) => setNewEmails({ ...newEmails, failure_recipients: v })}
          />

          {/* Successful runs: toggle + dedicated recipients */}
          <RecipientGroup
            label="Successful runs"
            description="Email after every passing run (off by default — usually noisy)"
            toggleChecked={settings.on_success}
            onToggle={(v) => setSettings({ ...settings, on_success: v })}
            recipients={settings.success_recipients || []}
            onAdd={(email) => addRecipient('success_recipients', email)}
            onRemove={(email) => removeRecipient('success_recipients', email)}
            inputValue={newEmails.success_recipients || ''}
            onInputChange={(v) => setNewEmails({ ...newEmails, success_recipients: v })}
          />

          <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
            Emails are sent via SparkPost (configured server-side via the <code className="bg-gray-100 px-1 rounded">SPARKPOST_API_KEY</code> environment variable). From: tech@bajaao.com.
          </p>
        </div>
      </div>

      {/* Jira Integration */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200">
          <Ticket className="h-5 w-5 text-gray-400" />
          <div>
            <h2 className="text-sm font-medium text-gray-800">Jira Integration</h2>
            <p className="text-xs text-gray-500">Automatically raise Jira tickets for failed tests</p>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Auto-file toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm text-gray-700">Auto-file Jira tickets</label>
              <p className="text-xs text-gray-500">
                File a ticket for each failed test. Repeats are deduped by signature, so the
                same failure never opens a second ticket. Web failures are assigned to you;
                mobile failures to the mobile owner.
              </p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, auto_file_jira: !settings.auto_file_jira })}
              className={clsx(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0',
                settings.auto_file_jira ? 'bg-primary-600' : 'bg-gray-300'
              )}
            >
              <span
                className={clsx(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  settings.auto_file_jira ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </div>

          {/* Outage threshold */}
          <div className={clsx('flex items-center justify-between', !settings.auto_file_jira && 'opacity-50 pointer-events-none')}>
            <div>
              <label className="text-sm text-gray-700">Outage threshold</label>
              <p className="text-xs text-gray-500">
                When a single run has this many failures or more, it's treated as an outage:
                one summary ticket per category (e.g. "Connect — 7 failures") instead of one per test.
              </p>
            </div>
            <input
              type="number"
              min="1"
              value={settings.jira_outage_threshold ?? 8}
              onChange={(e) => setSettings({ ...settings, jira_outage_threshold: parseInt(e.target.value, 10) || 1 })}
              className="w-20 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex items-center justify-end gap-3">
        {saved && (
          <span className="flex items-center gap-1 text-sm text-success-600">
            <CheckCircle className="h-4 w-4" />
            Saved
          </span>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className={clsx(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors',
            saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'
          )}
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

function RecipientGroup({ label, description, toggleChecked, onToggle, recipients, onAdd, onRemove, inputValue, onInputChange }) {
  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-3 bg-gray-50/40">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-800">{label}</p>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
        <button
          onClick={() => onToggle(!toggleChecked)}
          className={clsx(
            'relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0',
            toggleChecked ? 'bg-primary-600' : 'bg-gray-300'
          )}
        >
          <span
            className={clsx(
              'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
              toggleChecked ? 'translate-x-4.5' : 'translate-x-0.5'
            )}
          />
        </button>
      </div>

      {/* Recipients list */}
      <div className={clsx('space-y-2', !toggleChecked && 'opacity-50 pointer-events-none')}>
        <div className="flex flex-wrap gap-2 min-h-[1.5rem]">
          {recipients.length === 0 && (
            <span className="text-xs text-gray-400 italic">No recipients yet</span>
          )}
          {recipients.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full text-xs"
            >
              {email}
              <button
                onClick={() => onRemove(email)}
                className="text-gray-400 hover:text-error-600"
                title="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            placeholder="Add email address"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAdd(inputValue))}
          />
          <button
            type="button"
            onClick={() => onAdd(inputValue)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

export default Settings;
